import React, { useEffect, useState } from "react";
import { Col, Dropdown, DropdownDivider, Row } from "react-bootstrap";
import "../App.css";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FiLogOut } from "react-icons/fi";
import { FaFile, FaUserPlus } from "react-icons/fa";
import { GiChart } from "react-icons/gi";
import { IoDocumentAttach } from "react-icons/io5";
import logo from "../assets/images/logo.png";
import "react-notifications-component/dist/theme.css";

const PAGE_META = {
  "/dashboard": {
    title: "Dashboard",
    subtitle: "Group life upload coverage at a glance",
  },
  "/upload-file": {
    title: "Policies",
    subtitle: "Upload policies and push them to NIID and NAICOM",
  },
  "/add-User": {
    title: "Users",
    subtitle: "Manage the people who can use this portal",
  },
  "/roles": {
    title: "Roles",
    subtitle: "Group permissions and portal access",
  },
};

export const Root = () => {
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const userInfo = sessionStorage.getItem("userdata");
    setUserData(userInfo ? JSON.parse(userInfo) : null);

    const isAuth = sessionStorage.getItem("token");
    if (!isAuth) {
      navigate("/");
      return;
    }

    const logout = () => {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("userdata");
      navigate("/");
    };

    const parseJwt = (t) => {
      try {
        return JSON.parse(atob(t.split(".")[1]));
      } catch (e) {
        return null;
      }
    };

    const decoded = parseJwt(isAuth);
    if (decoded && decoded.exp) {
      const timeLeft = decoded.exp * 1000 - Date.now();

      if (timeLeft <= 0) {
        logout();
      } else {
        const timer = setTimeout(logout, timeLeft);
        return () => clearTimeout(timer);
      }
    }
  }, [navigate]);

  const meta =
    PAGE_META[location.pathname] ||
    (location.pathname.startsWith("/policy-details")
      ? { title: "Policy details", subtitle: "Policy and member records" }
      : { title: "Group Life", subtitle: "Group life policy administration" });

  const initials = `${userData?.firstName?.charAt(0) || ""}${
    userData?.lastName?.charAt(0) || ""
  }`.toUpperCase();

  return (
    <div className="app-shell">
      <Row className="g-0">
        <Col xs={12} lg={2} className="app-sidebar">
          <div className="app-brand">
            <img src={logo} alt="Sanlam Allianz" className="logo" />
            <div>
              <div className="app-brand__name">Group Life</div>
              <div className="app-brand__tag">Policy portal</div>
            </div>
          </div>

          <nav className="app-nav">
            <div className="app-nav__section">Manage</div>

            <NavLink to="/dashboard" className="app-nav__link">
              <GiChart /> Dashboard
            </NavLink>

            <NavLink to="/upload-file" className="app-nav__link">
              <FaFile /> Policies
            </NavLink>

            <NavLink to="/add-User" className="app-nav__link">
              <FaUserPlus /> Users
            </NavLink>

            <NavLink to="/roles" className="app-nav__link">
              <IoDocumentAttach /> Roles
            </NavLink>
          </nav>

          <div className="app-sidebar__footer">
            <div className="app-user">
              <div className="round-profile">{initials}</div>
              <div>
                <div className="app-user__name">
                  {userData?.firstName} {userData?.lastName}
                </div>
                <div className="app-user__role">{userData?.role?.name}</div>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} lg={10} className="app-main">
          <div className="app-topbar">
            <div>
              <div className="app-topbar__title">{meta.title}</div>
              <div className="surface-card__meta">{meta.subtitle}</div>
            </div>

            <Dropdown align="end">
              <Dropdown.Toggle
                variant="link"
                className="d-flex align-items-center gap-2 text-decoration-none"
                id="dropdown-basic"
              >
                <div className="round-profile">{initials}</div>
                <span className="d-none d-sm-inline" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {userData?.firstName} {userData?.lastName}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ fontSize: "0.78rem", borderRadius: "14px", border: "1px solid #e6ebf2", boxShadow: "0 12px 28px -10px rgba(15,23,42,0.16)" }}>
                <Dropdown.Item
                  onClick={() => {
                    sessionStorage.removeItem("token");
                    sessionStorage.removeItem("refreshToken");
                    sessionStorage.removeItem("userdata");
                    navigate("/");
                  }}
                  style={{ color: "var(--danger)" }}
                >
                  <FiLogOut /> Log out
                </Dropdown.Item>
                <DropdownDivider />
                <div style={{ padding: "6px 14px", color: "var(--ink-500)" }}>
                  {userData?.email}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="app-content">
            <Outlet />
          </div>
        </Col>
      </Row>
    </div>
  );
};
